/**
 * 发版后老页面加载老 chunk 会 404,页面当场崩掉——这里是它的兜底。
 *
 * 2026-08-30 生产实测:14:06 发版后,一个开着旧页面的商家在 14:43 点进"快捷生成",
 * 浏览器去要 `QuickGenerationPage-D2AwJEI2.js`(旧构建的文件名),而磁盘上只剩
 * `QuickGenerationPage-Dp888biC.js`,连着 3 个 chunk 全 404。当天一共 5 次,
 * 涉及快捷生成和展厅两个页面。用户看到的是白屏或点了没反应,必须自己刷新才好。
 *
 * 兜底就是"自动帮他刷一次"。唯一要小心的是**别刷成死循环**:如果刷新后依然失败
 * (CDN 还在发旧 index.html、文件是真没了、网络断了),再刷一次也是同样结果,
 * 只会把页面卡在无限重载里——那比白屏更糟。所以带一个会话级节流。
 */

/** 刷新节流窗口。同一标签页内这段时间里最多自动刷一次。 */
export const CHUNK_RELOAD_THROTTLE_MS = 30_000;
export const CHUNK_RELOAD_STORAGE_KEY = "spa:chunk-reload-at";

/**
 * 这个错误是不是"动态 import 的 chunk 没加载成功"。
 *
 * 各浏览器文案不一样,而且都没有稳定的错误码可认,只能匹配文案。宁可漏判也不要错判:
 * 把普通的业务异常当成 chunk 失效会导致莫名其妙的自动刷新,用户填的表单就没了。
 */
export function isChunkLoadFailure(reason: unknown): boolean {
  const message =
    reason instanceof Error
      ? `${reason.name}: ${reason.message}`
      : typeof reason === "string"
        ? reason
        : "";
  if (!message) {
    return false;
  }
  return (
    // Chrome / Edge
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    // Firefox
    message.includes("error loading dynamically imported module") ||
    // Safari
    message.includes("Importing a module script failed") ||
    // webpack 时代的老写法,少数嵌入式浏览器仍在用
    message.includes("ChunkLoadError")
  );
}

/**
 * 现在该不该为 chunk 失效自动刷新。
 *
 * 纯函数,时间和"上次刷新时刻"都由调用方传进来——这样节流逻辑本身可以直接用单测钉住,
 * 不必去模拟 location.reload。
 */
export function shouldReloadForChunkFailure(now: number, lastReloadAt: number | null): boolean {
  if (lastReloadAt === null) {
    return true;
  }
  if (!Number.isFinite(lastReloadAt)) {
    return true;
  }
  // 时钟回拨或存进去的是未来时间:当成"刚刷过",宁可不刷也不要循环。
  if (lastReloadAt > now) {
    return false;
  }
  return now - lastReloadAt >= CHUNK_RELOAD_THROTTLE_MS;
}

function readLastReloadAt(storage: Pick<Storage, "getItem">): number | null {
  try {
    const raw = storage.getItem(CHUNK_RELOAD_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // 隐私模式/禁用站点数据时 sessionStorage 存取会直接抛。读不到就当没刷过。
    return null;
  }
}

function writeLastReloadAt(storage: Pick<Storage, "setItem">, at: number): void {
  try {
    storage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(at));
  } catch {
    // 写不进去就退化成"没有节流"。比不兜底强,循环风险由下面的 once 兜住。
  }
}

export type ChunkReloadGuardOptions = {
  storage?: Pick<Storage, "getItem" | "setItem">;
  now?: () => number;
  reload?: () => void;
  /**
   * 挂监听的目标,默认 `window`。做成可注入是为了能测:仓库的 vitest 跑在 node 环境、
   * 没有装 jsdom(`.tsx` 用例走的是 renderToStaticMarkup),拿不到真 window。
   * 传一个普通 EventTarget 就能把事件流本身钉住,而不是只测两个纯函数、
   * 让"事件接错了名字"这类回落从守卫底下溜过去。
   */
  target?: EventTarget;
};

/**
 * 装上全局监听。返回一个卸载函数(测试用)。
 *
 * 两个入口都要接:
 * - `vite:preloadError`:Vite 对 `<link rel=modulepreload>` 失败的专用事件,能在
 *   真正 import 之前就拦到,是最干净的一条;
 * - `unhandledrejection`:懒加载组件在 React.lazy 里失败时走这条。项目里的 `lazy()`
 *   散在 5 个文件、几十个路由上,逐个包一层重试既啰嗦又必然漏掉新加的——在顶上收口。
 */
export function installChunkReloadGuard(options: ChunkReloadGuardOptions = {}): () => void {
  const target = options.target ?? (typeof window === "undefined" ? undefined : window);
  if (!target) {
    return () => {};
  }
  const now = options.now ?? (() => Date.now());
  const reload = options.reload ?? (() => window.location.reload());
  const storage = options.storage ?? (typeof window === "undefined" ? undefined : window.sessionStorage);
  if (!storage) {
    return () => {};
  }
  // 同一次加载里只允许触发一次,避免一个页面同时缺三个 chunk 时连发三次 reload。
  let reloading = false;

  const attempt = (reason: unknown): void => {
    if (reloading || !isChunkLoadFailure(reason)) {
      return;
    }
    const at = now();
    if (!shouldReloadForChunkFailure(at, readLastReloadAt(storage))) {
      // 刚刷过还是失败,说明刷新救不了。留给错误边界去显示,别再刷。
      return;
    }
    reloading = true;
    writeLastReloadAt(storage, at);
    reload();
  };

  const onPreloadError = (event: Event): void => {
    attempt((event as Event & { payload?: unknown }).payload ?? event);
  };
  const onRejection = (event: PromiseRejectionEvent): void => {
    attempt(event.reason);
  };

  target.addEventListener("vite:preloadError", onPreloadError);
  target.addEventListener("unhandledrejection", onRejection as EventListener);
  return () => {
    target.removeEventListener("vite:preloadError", onPreloadError);
    target.removeEventListener("unhandledrejection", onRejection as EventListener);
  };
}
